<script>
import { clienteComputed } from "@/state/helpers";

/**
 * Barra superior del portal de clientes.
 *
 * Deliberadamente no reutiliza `nav-bar.vue` ni `side-bar.vue`: esos componentes
 * son del panel de administración y arrastran el menú interno, el buscador de
 * empleados y la barra lateral. El cliente no debe ver nada de eso.
 */
export default {
  data() {
    return {
      menuAbierto: false,
      // Contenido que la app agrupa bajo la pestaña "Comercios".
      comercios: [
        { nombre: "portal-negocios", texto: "Directorio", icono: "mdi mdi-storefront" },
        { nombre: "portal-eventos", texto: "Qué plan hoy", icono: "mdi mdi-calendar-star" },
        { nombre: "portal-vacantes", texto: "Vacantes", icono: "mdi mdi-briefcase" },
      ],
    };
  },
  computed: {
    ...clienteComputed,
    /** El desplegable "Comercios" se ilumina si la ruta activa es uno de sus hijos. */
    enComercios() {
      return this.comercios.some((c) => c.nombre === this.$route.name);
    },
  },
  watch: {
    // Sin esto el menú desplegado en móvil se queda abierto encima del
    // contenido después de navegar.
    $route() {
      this.menuAbierto = false;
    },
  },
  methods: {
    async salir() {
      await this.$store.dispatch("auth/logOut");
      this.$store.dispatch("cliente/limpiar");
      this.$store.dispatch("negocio/limpiar");
      this.$router.push({ name: "portal-login" });
    },
  },
};
</script>

<template>
  <header class="portal-header">
    <div class="portal-header-inner">
      <router-link :to="{ name: 'portal' }" class="portal-marca">
        <img :src="require('@/assets/images/RCCN-LOGO-WHITE1.png')" alt="Recnet" class="portal-logo" />
      </router-link>

      <button class="portal-toggle d-md-none" type="button" @click="menuAbierto = !menuAbierto"
        aria-label="Abrir menú">
        <i class="mdi" :class="menuAbierto ? 'mdi-close' : 'mdi-menu'"></i>
      </button>

      <nav class="portal-nav" :class="{ 'portal-nav-abierto': menuAbierto }">
        <!-- Mientras el registro esté incompleto sólo se muestra Network, para
             que el recién registrado se concentre en completarlo. -->
        <template v-if="!perfilIncompleto">
          <router-link :to="{ name: 'portal' }" class="portal-link" exact>
            <i class="mdi mdi-home"></i> Home
          </router-link>

          <b-dropdown variant="link" toggle-class="portal-link" :class="{ 'comercios-activo': enComercios }"
            menu-class="portal-menu" no-caret>
            <template #button-content>
              <i class="mdi mdi-storefront"></i> Comercios <i class="mdi mdi-chevron-down"></i>
            </template>
            <b-dropdown-item v-for="c in comercios" :key="c.nombre" :to="{ name: c.nombre }">
              <i :class="c.icono"></i> {{ c.texto }}
            </b-dropdown-item>
          </b-dropdown>

          <router-link :to="{ name: 'portal-sesiones' }" class="portal-link">
            <i class="mdi mdi-play-circle"></i> Producciones
          </router-link>
        </template>

        <router-link :to="{ name: 'portal-cuenta' }" class="portal-link">
          <i class="mdi mdi-account-circle"></i> Network
        </router-link>

        <div class="portal-cuenta">
          <button type="button" class="portal-link portal-salir" @click="salir">
            <i class="mdi mdi-logout"></i> Salir
          </button>
        </div>
      </nav>
    </div>
  </header>
</template>

<style scoped>
.portal-header {
  /* Degradado de marca RecNet, muestreado del banner "barra de menu":
     teal → morado → magenta. */
  background: linear-gradient(
    90deg,
    #1ba291 0%,
    #299c92 10%,
    #458297 25%,
    #5f439e 50%,
    #652a96 65%,
    #692d81 80%,
    #6a4368 100%
  );
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.portal-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 64px;
}

.portal-marca {
  display: flex;
  align-items: center;
  margin-right: auto;
}

.portal-logo {
  height: 34px;
  width: auto;
}

.portal-toggle {
  background: none;
  border: 0;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  padding: 4px 8px;
}

.portal-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.portal-link {
  color: rgba(255, 255, 255, 0.85);
  padding: 10px 14px;
  border-radius: 6px;
  font-weight: 500;
  white-space: nowrap;
  background: none;
  border: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.portal-link:hover,
.portal-link.router-link-active {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  text-decoration: none;
}

/*
 * El botón del desplegable "Comercios" lo renderiza BootstrapVue, no esta
 * plantilla, así que el estilo con ámbito no lo alcanza: hay que usar `>>>`.
 * Sin esto, el botón conservaba el color azul de `.btn-link` y apenas se veía
 * sobre el morado de la barra.
 */
.portal-nav >>> .b-dropdown .btn-link {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  padding: 10px 14px;
  border-radius: 6px;
  text-decoration: none;
}

.portal-nav >>> .b-dropdown .btn-link:hover,
.portal-nav >>> .b-dropdown .btn-link:focus,
.portal-nav >>> .b-dropdown.comercios-activo .btn-link,
.portal-nav >>> .b-dropdown.show .btn-link {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

.portal-cuenta {
  display: flex;
  align-items: center;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

@media (max-width: 767.98px) {
  .portal-nav {
    display: none;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    padding-bottom: 12px;
  }

  .portal-nav-abierto {
    display: flex;
  }

  .portal-cuenta {
    flex-direction: column;
    align-items: stretch;
    margin-left: 0;
    padding-left: 0;
    border-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    margin-top: 8px;
    padding-top: 8px;
  }

  .portal-link {
    text-align: left;
  }
}
</style>
